<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import { attendanceStatusClass, attendanceStatusLabel, formatNumber, monthEnd, monthStart, toDateInput } from '../utils/format'
import type { AttendanceRecord, Employee, TimesheetEntry, TimesheetReportRow } from '../types/hr'

const { t } = useI18n()
const { listAllEmployees, listAllAttendance, listAllTimesheets, timesheetReport } = useHrApi()

const STANDARD_DAY_HOURS = 8

const employees = ref<Employee[]>([])
const attendance = ref<AttendanceRecord[]>([])
const timesheets = ref<TimesheetEntry[]>([])
const report = ref<TimesheetReportRow[]>([])
const loading = ref(true)
const error = ref('')

const today = toDateInput(new Date())
const from = monthStart()

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [employeeRows, attendanceRows, timesheetRows, reportRows] = await Promise.all([
      listAllEmployees(),
      listAllAttendance({ date_from: today, date_to: today }),
      listAllTimesheets({ date_from: from, date_to: monthEnd() }),
      timesheetReport({ date_from: from, date_to: monthEnd() }),
    ])
    employees.value = employeeRows
    attendance.value = attendanceRows
    timesheets.value = timesheetRows
    report.value = reportRows
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)

const activeEmployees = computed(() => employees.value.filter((employee) => employee.is_active === 1))
const departmentCount = computed(
  () => new Set(employees.value.map((employee) => employee.department_agent_id).filter(Boolean)).size,
)
const totalHours = computed(() => timesheets.value.reduce((sum, entry) => sum + entry.hours_worked, 0))
const workforce = computed(() => new Map(activeEmployees.value.map((employee) => [employee.id, employee])))

const dailyHours = computed(() => {
  const map = new Map<number, number>()
  for (const entry of timesheets.value) {
    map.set(entry.employee_id, (map.get(entry.employee_id) ?? 0) + entry.hours_worked)
  }
  return map
})

const overtimeHours = computed(() => {
  const perDay = new Map<string, number>()
  for (const entry of timesheets.value) {
    const key = `${entry.employee_id}:${entry.work_date}`
    perDay.set(key, (perDay.get(key) ?? 0) + entry.hours_worked)
  }
  let total = 0
  for (const hours of perDay.values()) {
    total += Math.max(0, hours - STANDARD_DAY_HOURS)
  }
  return total
})

const statusCounts = computed(() => {
  const counts: Record<string, number> = {}
  for (const record of attendance.value) {
    counts[record.status] = (counts[record.status] ?? 0) + 1
  }
  return counts
})

const topPerformers = computed(() =>
  [...report.value]
    .sort((a, b) => b.total_hours - a.total_hours)
    .filter((row) => row.total_hours > 0)
    .slice(0, 6),
)

const todaySnapshot = computed(() =>
  [...attendance.value]
    .sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''))
    .slice(0, 8),
)

function departmentOf(record: AttendanceRecord): string {
  return record.department_name || workforce.value.get(record.employee_id)?.department_name || '—'
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.overview.title') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('hr.overview.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-value">{{ employees.length.toLocaleString() }}</span>
        <span class="summary-label">{{ t('hr.overview.headcount') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ activeEmployees.length.toLocaleString() }}</span>
        <span class="summary-label">{{ t('hr.overview.active') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ departmentCount.toLocaleString() }}</span>
        <span class="summary-label">{{ t('hr.overview.departments') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ (statusCounts.present ?? 0).toLocaleString() }}</span>
        <span class="summary-label">{{ t('hr.overview.presentToday') }}</span>
      </div>
      <div class="summary-card attention">
        <span class="summary-value">{{ (statusCounts.absent ?? 0).toLocaleString() }}</span>
        <span class="summary-label">{{ t('hr.overview.absentToday') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ (statusCounts.leave ?? 0).toLocaleString() }}</span>
        <span class="summary-label">{{ t('hr.overview.onLeaveToday') }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-value">{{ formatNumber(totalHours, 1) }}</span>
        <span class="summary-label">{{ t('hr.overview.hoursThisMonth') }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-value">{{ formatNumber(overtimeHours, 1) }}</span>
        <span class="summary-label">{{ t('hr.overview.overtimeHours') }}</span>
      </div>
    </div>

    <h3 class="section-title">{{ t('hr.overview.quickActions') }}</h3>
    <div class="quick-grid">
      <RouterLink class="quick-card" to="/employees/new">
        <strong>{{ t('hr.overview.addEmployee') }}</strong>
        <span>{{ t('hr.employees.description') }}</span>
      </RouterLink>
      <RouterLink class="quick-card" to="/attendance">
        <strong>{{ t('hr.overview.openAttendance') }}</strong>
        <span>{{ t('hr.attendance.description') }}</span>
      </RouterLink>
      <RouterLink class="quick-card" to="/leave">
        <strong>{{ t('hr.overview.openLeave') }}</strong>
        <span>{{ t('hr.leave.description') }}</span>
      </RouterLink>
      <RouterLink class="quick-card" to="/overtime">
        <strong>{{ t('hr.overview.openOvertime') }}</strong>
        <span>{{ t('hr.overtime.description') }}</span>
      </RouterLink>
    </div>

    <h3 class="section-title">{{ t('hr.overview.todaySnapshot') }}</h3>
    <div v-if="todaySnapshot.length === 0" class="panel">
      <span class="empty-inline">{{ t('hr.overview.noToday') }}</span>
    </div>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.common.employee') }}</th>
            <th>{{ t('hr.common.department') }}</th>
            <th>{{ t('hr.common.status') }}</th>
            <th>{{ t('hr.attendance.checkIn') }}</th>
            <th>{{ t('hr.attendance.worked') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="record in todaySnapshot" :key="record.id">
            <td class="cell-name">
              {{ record.full_name }}
              <small class="cell-sub">{{ record.employee_code }}</small>
            </td>
            <td>{{ departmentOf(record) }}</td>
            <td>
              <span :class="['status-badge', attendanceStatusClass(record.status)]">
                {{ attendanceStatusLabel(t, record.status) }}
              </span>
            </td>
            <td class="cell-date">{{ record.check_in_at ? record.check_in_at.slice(11, 16) : '—' }}</td>
            <td class="cell-num">{{ formatNumber(record.worked_minutes / 60, 1) }} h</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 class="section-title">{{ t('hr.overview.topPerformers') }}</h3>
    <div v-if="topPerformers.length === 0" class="panel">
      <span class="empty-inline">{{ t('hr.overview.noPerformers') }}</span>
    </div>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.common.employee') }}</th>
            <th>{{ t('hr.common.hours') }}</th>
            <th>{{ t('hr.overtime.entries') }}</th>
            <th>{{ t('hr.overtime.billAmount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in topPerformers" :key="row.employee_id">
            <td class="cell-name">
              {{ row.full_name }}
              <small class="cell-sub">{{ row.employee_code }}</small>
            </td>
            <td class="cell-num">{{ formatNumber(row.total_hours, 1) }}</td>
            <td class="cell-num">{{ row.total_entries }}</td>
            <td class="cell-num">{{ formatNumber(row.total_bill_amount) }} {{ row.currency_code ?? '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>

<style scoped>
.section-title { margin: 22px 0 10px; font-size: 14px; font-weight: 700; color: #2b2027; }
</style>
